import { clientScopeOf } from "../../shell/modes/clientScope.ts";
import type { EmailPlace } from "../data.ts";
import type { ReaderTab } from "./EmailReader.tsx";

/** The search parameter that names the email the reader shows, which a notification's link carries (022 FR-022-357). */
export const EMAIL_PARAM = "email";

/** The reader's tab other than Email, named beside the email it shows: Insights, from a row's count of insights to review, or the tab the reader moved to (2026-10-07). */
export const EMAIL_TAB_PARAM = "email_tab";

/**
 * The list a page of the shell reads emails in, whose `?email=` the reader opens over the page
 * (022 FR-022-364 as amended 2026-10-07): a client's page, whose Emails section lists the client's,
 * and the Unassigned emails; none elsewhere. It is read from the address alone, so the shell's peek
 * slot opens the reader before the page's list has loaded, as a notification's link asks.
 */
export function emailPlaceOf(ws: string, pathname: string): EmailPlace | null {
  if (!pathname.startsWith(`/${ws}/`)) return null;
  const rest = pathname.slice(`/${ws}/`.length).replace(/\/$/u, "");
  if (rest === "unassigned-emails") return { kind: "unassigned" };
  const { accountId } = clientScopeOf(ws, pathname);
  return accountId !== null && rest.split("/").length === 2 ? { kind: "client", accountId } : null;
}

/** The page the place's emails are listed on, where the reader opens over the list. */
export function emailListPath(ws: string, place: EmailPlace): string {
  return place.kind === "client" ? `/${ws}/clients/${encodeURIComponent(place.accountId)}` : `/${ws}/unassigned-emails`;
}

/** An email's full page, under its client or under the Unassigned emails, so the rail stays on Clients (2026-10-07). */
export function emailPagePath(ws: string, place: EmailPlace, emailId: string): string {
  const email = encodeURIComponent(emailId);
  return place.kind === "client" ? `${emailListPath(ws, place)}/emails/${email}` : `${emailListPath(ws, place)}/${email}`;
}

/** The reader's tab the address names, Email when it names none or one the reader does not have. */
export function readerTabOf(search: URLSearchParams): ReaderTab {
  const named = search.get(EMAIL_TAB_PARAM);
  return named === "insights" || named === "activity" ? named : "email";
}

/** The address's parameters with the reader's tab named, or dropped for Email, the tab a reading opens on. */
export function withReaderTab(search: URLSearchParams, tab: ReaderTab): URLSearchParams {
  const next = new URLSearchParams(search);
  if (tab === "email") next.delete(EMAIL_TAB_PARAM);
  else next.set(EMAIL_TAB_PARAM, tab);
  return next;
}
